import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, MenuItem, Select, FormControl,
  InputLabel, Chip, FormControlLabel, Switch, SelectChangeEvent, Alert, Tooltip,
} from '@mui/material';
import { Add, Edit, VpnKey, PhonelinkLock, Delete } from '@mui/icons-material';
import api from '../../api/client';

interface UserRecord {
  id: number; username: string; full_name: string; email: string;
  role: string; is_active: boolean; dispatch_access: boolean;
  mobile_access: boolean; admin_access: boolean; superadmin_access: boolean;
  require_2fa: boolean; totp_configured: boolean;
}

const ROLES = ['superadmin', 'admin', 'dispatcher', 'field'];

export default function UsersPage() {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<UserRecord | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetUser, setResetUser] = useState<UserRecord | null>(null);
  const [resetPw, setResetPw] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  const [form, setForm] = useState({
    username: '', password: '', full_name: '', email: '', role: 'dispatcher',
    dispatch_access: false, mobile_access: false, admin_access: false,
    superadmin_access: false, require_2fa: false,
  });

  const load = () => api.get('/users').then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const handleOpen = (u?: UserRecord) => {
    if (u) {
      setEditing(u);
      setForm({
        username: u.username, password: '', full_name: u.full_name, email: u.email,
        role: u.role, dispatch_access: u.dispatch_access, mobile_access: u.mobile_access,
        admin_access: u.admin_access, superadmin_access: u.superadmin_access, require_2fa: u.require_2fa,
      });
    } else {
      setEditing(null);
      setForm({
        username: '', password: '', full_name: '', email: '', role: 'dispatcher',
        dispatch_access: true, mobile_access: false, admin_access: false,
        superadmin_access: false, require_2fa: false,
      });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (editing) {
      const { username, password, ...updateData } = form;
      await api.patch(`/users/${editing.id}`, updateData);
    } else {
      await api.post('/users', form);
    }
    setOpen(false);
    load();
  };

  const handleResetPassword = async () => {
    setResetError('');
    setResetSuccess('');
    if (!resetPw) { setResetError('Password is required'); return; }
    if (resetPw.length < 6) { setResetError('Password must be at least 6 characters'); return; }
    if (resetPw !== resetConfirm) { setResetError('Passwords do not match'); return; }
    try {
      await api.post(`/users/${resetUser!.id}/reset-password`, { new_password: resetPw });
      setResetSuccess(`Password reset for ${resetUser!.username}. They will be required to change it on next login.`);
      setResetPw(''); setResetConfirm('');
      setTimeout(() => { setResetOpen(false); setResetSuccess(''); }, 2500);
    } catch (err: any) {
      setResetError(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDelete = async (u: UserRecord) => {
    if (!window.confirm(`Permanently delete user "${u.username}"? This cannot be undone. Their past calls will be kept but no longer linked to this user.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      load();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetTotp = async (u: UserRecord) => {
    if (!window.confirm(`Reset TOTP for ${u.username}? They will need to set up their authenticator app again on next login.`)) return;
    try {
      await api.post(`/users/${u.id}/reset-totp`);
      load();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to reset TOTP');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Users</Typography>
        <Button startIcon={<Add />} variant="contained" onClick={() => handleOpen()}>Add User</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Access</TableCell>
              <TableCell>2FA</TableCell>
              <TableCell>Status</TableCell>
              <TableCell width={60} />
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell sx={{ fontWeight: 700 }}>{u.username}</TableCell>
                <TableCell>{u.full_name}</TableCell>
                <TableCell><Chip size="small" label={u.role} /></TableCell>
                <TableCell>
                  {u.dispatch_access && <Chip size="small" label="Dispatch" sx={{ mr: 0.5 }} color="primary" />}
                  {u.mobile_access && <Chip size="small" label="Mobile" sx={{ mr: 0.5 }} color="secondary" />}
                  {u.admin_access && <Chip size="small" label="Admin" sx={{ mr: 0.5 }} color="warning" />}
                  {u.superadmin_access && <Chip size="small" label="Super" color="error" />}
                </TableCell>
                <TableCell>
                  {u.totp_configured
                    ? <Chip size="small" label="Active" color="success" />
                    : u.require_2fa
                      ? <Chip size="small" label="Pending Setup" color="warning" />
                      : <Chip size="small" label="Off" variant="outlined" />}
                </TableCell>
                <TableCell><Chip size="small" label={u.is_active ? 'Active' : 'Disabled'} color={u.is_active ? 'success' : 'default'} /></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Reset Password"><IconButton size="small" onClick={() => { setResetUser(u); setResetPw(''); setResetConfirm(''); setResetError(''); setResetSuccess(''); setResetOpen(true); }}><VpnKey fontSize="small" /></IconButton></Tooltip>
                  {u.totp_configured && <Tooltip title="Reset TOTP"><IconButton size="small" color="warning" onClick={() => handleResetTotp(u)}><PhonelinkLock fontSize="small" /></IconButton></Tooltip>}
                  <IconButton size="small" onClick={() => handleOpen(u)}><Edit fontSize="small" /></IconButton>
                  <Tooltip title="Delete User"><IconButton size="small" color="error" onClick={() => handleDelete(u)}><Delete fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit User' : 'New User'}</DialogTitle>
        <DialogContent>
          {!editing && (
            <>
              <TextField fullWidth label="Username" margin="normal" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
              <TextField fullWidth label="Password" type="password" margin="normal" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </>
          )}
          <TextField fullWidth label="Full Name" margin="normal" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
          <TextField fullWidth label="Email" margin="normal" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select value={form.role} label="Role" onChange={(e: SelectChangeEvent) => {
              const newRole = e.target.value;
              const updates: any = { role: newRole };
              if (newRole === 'dispatcher') { updates.dispatch_access = true; }
              else if (newRole === 'admin') { updates.dispatch_access = true; updates.admin_access = true; }
              else if (newRole === 'superadmin') { updates.dispatch_access = true; updates.admin_access = true; updates.superadmin_access = true; }
              else if (newRole === 'field') { updates.mobile_access = true; }
              setForm({ ...form, ...updates });
            }}>
              {ROLES.map((r) => <MenuItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Access Permissions</Typography>
          <FormControlLabel control={<Switch checked={form.dispatch_access} onChange={(_, c) => setForm({ ...form, dispatch_access: c })} />} label="Dispatch Console Access" />
          <FormControlLabel control={<Switch checked={form.mobile_access} onChange={(_, c) => setForm({ ...form, mobile_access: c })} />} label="Mobile App Access" />
          <FormControlLabel control={<Switch checked={form.admin_access} onChange={(_, c) => setForm({ ...form, admin_access: c })} />} label="Admin Portal Access" />
          <FormControlLabel control={<Switch checked={form.superadmin_access} onChange={(_, c) => setForm({ ...form, superadmin_access: c })} />} label="Super-Admin Access" />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Security</Typography>
          <FormControlLabel control={<Switch checked={form.require_2fa} onChange={(_, c) => setForm({ ...form, require_2fa: c })} />} label="Require Two-Factor Authentication" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password — {resetUser?.username}</DialogTitle>
        <DialogContent>
          {resetError && <Alert severity="error" sx={{ mb: 1 }}>{resetError}</Alert>}
          {resetSuccess && <Alert severity="success" sx={{ mb: 1 }}>{resetSuccess}</Alert>}
          <TextField fullWidth label="New Password" type="password" margin="normal" value={resetPw} onChange={(e) => setResetPw(e.target.value)} autoFocus />
          <TextField fullWidth label="Confirm Password" type="password" margin="normal" value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleResetPassword(); }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleResetPassword}>Reset Password</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
